/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { type ReadonlyAbort, ReportableError, requestWithRetry } from '@amazon/vinyl-util'
import { isAbortError } from '@/blackbolt/agent/agentUtil'
import { buildAuthHeaders, getAgentBaseUrl } from '@/blackbolt/agent/claw/clawHttp'

// Claw init POST (`/agents/claw/init`). Runs ONCE per session (memoized by the
// connection manager): it validates auth, seeds the workspace, and (critically)
// sets the pod-affinity cookie every subsequent WS handshake rides on — so a
// socket the server closed after a turn reopens without re-init. Split out of
// `clawAdapter` so the adapter composes the init + socket phases without owning
// either's HTTP details.

export interface InitResponse {
    readonly pod?: string
    readonly customerId?: string
    readonly sessionId?: string
}

/**
 * Init-failure carrier — attaches the HTTP status (if any) so recipients can
 * distinguish 404 (endpoint not live) from network failure (undefined) from
 * a real 5xx.
 */
export class InitFailedError extends ReportableError {
    get [Symbol.toStringTag](): string {
        return 'InitFailedError'
    }

    constructor(
        message: string,
        readonly status: number | undefined
    ) {
        super(message)
        Object.setPrototypeOf(this, InitFailedError.prototype)
    }
}

export async function callInit(abort: ReadonlyAbort | undefined): Promise<InitResponse> {
    const headers = await buildAuthHeaders()
    let response: Response
    try {
        response = await requestWithRetry(
            `${getAgentBaseUrl()}/agents/claw/init`,
            { method: 'POST', headers, body: '{}' },
            { abort }
        )
    } catch (err) {
        if (isAbortError(err)) throw err
        const status = (err as { response?: Response | null }).response?.status
        throw new InitFailedError(
            status ? `Init failed: ${status}` : `Init network error: ${String(err)}`,
            status
        )
    }
    const bodyText = await response.text()
    if (!bodyText) return {}
    try {
        return JSON.parse(bodyText) as InitResponse
    } catch {
        return {}
    }
}
